import type { VestiaireCatalogNodeKind, VestiaireSearchCriteriaInput } from '@/shared/api/types/generated';

// ============================================================================
// Arbre des catégories Vestiaire → Cascader antd. ⚠️ Un id level2 peut ÉGALER un id
// level1 (espaces distincts chez VC) : toute clé de nœud est `kind:id`, jamais l'id seul.
// ============================================================================

export type CatalogNodeKey = `${VestiaireCatalogNodeKind}:${number}`;

export interface VestiaireCatalogNodeLike {
  id: number;
  kind: VestiaireCatalogNodeKind;
  title: string;
  children?: VestiaireCatalogNodeLike[] | null;
}

export interface CascaderOption {
  value: CatalogNodeKey;
  label: string;
  children?: CascaderOption[];
}

type CatalogCriteria = Pick<
  VestiaireSearchCriteriaInput,
  'universeIds' | 'catalogRootIds' | 'catalogLevel1Ids' | 'catalogLevel2Ids'
>;

// Liste runtime des kinds (les enums générés sont des unions de chaînes, pas d'objet runtime).
const CATALOG_NODE_KINDS = ['universe', 'root', 'level1', 'level2'] as const satisfies readonly VestiaireCatalogNodeKind[];

// Kinds CONNUS (exclut le membre `%future added value` ajouté par le codegen à chaque enum
// généré) : c'est le seul sous-ensemble que FIELD_BY_KIND doit couvrir, et le seul que
// parseCatalogNodeKey peut retourner — une clé portant `%future added value` est rejetée.
type ConcreteCatalogNodeKind = (typeof CATALOG_NODE_KINDS)[number];

/** Champ du critère qui reçoit un id, selon le `kind` de son nœud. */
const FIELD_BY_KIND: Record<ConcreteCatalogNodeKind, keyof CatalogCriteria> = {
  universe: 'universeIds',
  root: 'catalogRootIds',
  level1: 'catalogLevel1Ids',
  level2: 'catalogLevel2Ids',
};

const isCatalogNodeKind = (value: string): value is ConcreteCatalogNodeKind =>
  (CATALOG_NODE_KINDS as readonly string[]).includes(value);

export const catalogNodeKey = (kind: VestiaireCatalogNodeKind, id: number): CatalogNodeKey => `${kind}:${id}`;

export const parseCatalogNodeKey = (key: string): { kind: ConcreteCatalogNodeKind; id: number } | null => {
  const [kind, rawId, ...rest] = key.split(':');
  if (rest.length > 0 || kind === undefined || !isCatalogNodeKind(kind) || !/^\d+$/.test(rawId ?? '')) return null;
  return { kind, id: Number(rawId) };
};

export const toCascaderOptions = (nodes: VestiaireCatalogNodeLike[]): CascaderOption[] =>
  nodes.map((node) => {
    const option: CascaderOption = { value: catalogNodeKey(node.kind, node.id), label: node.title };
    if (node.children?.length) option.children = toCascaderOptions(node.children);
    return option;
  });

/** Chemins cochés (Cascader multiple) → champs du critère. Plusieurs niveaux cochés = OU côté serveur. */
export const pathsToCatalogCriteria = (paths: CatalogNodeKey[][]): CatalogCriteria => {
  const byField = new Map<keyof CatalogCriteria, Set<number>>();
  for (const path of paths) {
    const leaf = path.length ? parseCatalogNodeKey(path[path.length - 1]) : null;
    if (!leaf) continue;
    const field = FIELD_BY_KIND[leaf.kind];
    const ids = byField.get(field) ?? new Set<number>();
    ids.add(leaf.id);
    byField.set(field, ids);
  }
  const criteria: CatalogCriteria = {};
  for (const [field, ids] of byField) criteria[field] = [...ids];
  return criteria;
};

/** Critère stocké → chemins complets pour préremplir le Cascader (édition). */
export const catalogCriteriaToPaths = (
  criteria: CatalogCriteria,
  nodes: VestiaireCatalogNodeLike[],
): CatalogNodeKey[][] => {
  const wanted = new Set<CatalogNodeKey>();
  for (const kind of CATALOG_NODE_KINDS) {
    const field = FIELD_BY_KIND[kind];
    for (const id of criteria[field] ?? []) wanted.add(catalogNodeKey(kind, id));
  }
  const paths: CatalogNodeKey[][] = [];
  const walk = (level: VestiaireCatalogNodeLike[], prefix: CatalogNodeKey[]) => {
    for (const node of level) {
      const path = [...prefix, catalogNodeKey(node.kind, node.id)];
      if (wanted.has(path[path.length - 1])) paths.push(path);
      if (node.children?.length) walk(node.children, path);
    }
  };
  walk(nodes, []);
  return paths;
};

/**
 * Ids de catégorie stockés absents de l'arbre actuel (chaque id compté dans l'espace de SON
 * kind). `catalogCriteriaToPaths` les écarte : la lecture seule s'en sert pour le signaler
 * plutôt que de les faire disparaître sans un mot.
 */
export const countMissingCatalogNodes = (criteria: CatalogCriteria, nodes: VestiaireCatalogNodeLike[]): number => {
  const present = new Set<CatalogNodeKey>();
  const walk = (level: VestiaireCatalogNodeLike[]) => {
    for (const node of level) {
      present.add(catalogNodeKey(node.kind, node.id));
      if (node.children?.length) walk(node.children);
    }
  };
  walk(nodes);
  let missing = 0;
  for (const kind of CATALOG_NODE_KINDS) {
    for (const id of new Set(criteria[FIELD_BY_KIND[kind]] ?? [])) {
      if (!present.has(catalogNodeKey(kind, id))) missing += 1;
    }
  }
  return missing;
};
